import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { Collapse } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CircleCheck,
  CircleDashed,
  ListChecks,
  MessageSquare,
  MessageSquarePlus,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "../ds";
import { useIsAuthorized } from "../hooks/useIsAuthorized";
import { useIsCustodian } from "../hooks/useIsAdmin";
import { useCurrentUserProfile } from "../hooks/useUserProfile";
import {
  invalidateSuggestionCaches,
  recordDepartmentImplementation,
} from "../api/ess";
import { displayDepartment } from "../api/departments";
import { RecordThread, type RecordThreadEntry } from "./RecordSummary";
import { formatDateTime } from "../utils/format";
import { apiErrorMessage } from "../utils/apiError";
import {
  suggestionEvaluations,
  suggestionImplementation,
  verifierDepartments,
  type DepartmentImplementation,
  type Suggestion,
} from "../types/ess";

// The stage where departments report in. `record_department_implementation`
// is a self-transition on this state alone; once every nominated department
// is done the backend moves the suggestion on by itself, which is what
// makes the card read-only from `implemented` forward.
const REPORTING_STATE = "accepted_by_sc";

// Every state from the reporting stage onward keeps the card visible as
// the record of who implemented what. `under_verification` is a legacy
// state, kept so suggestions that predate the auto-transition still show.
const VISIBLE_STATES = new Set([
  REPORTING_STATE,
  "implemented",
  "under_verification",
  "implementation_closed",
]);

// UI cap on one department comment (the backend accepts up to 2000).
const COMMENT_MAX = 500;

// One in-flight change, so a department saving doesn't freeze the others.
interface ImplementationChange {
  department: string;
  implemented?: boolean;
  comment?: string;
}

function SectionHeader({ title, extra }: { title: string; extra?: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 2 }}>
      <Box
        sx={(theme) => ({
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "primary.main",
        })}
      >
        <RavenIcon icon={ListChecks} sx={{ fontSize: 16 }} />
      </Box>
      <Typography
        sx={{ fontSize: 16, fontWeight: 600, color: "primary.main", flex: 1 }}
      >
        {title}
      </Typography>
      {extra && (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {extra}
        </Typography>
      )}
    </Stack>
  );
}

function ProgressSummary({ done, total }: { done: number; total: number }) {
  const label = `${done} of ${total} departments implemented`;
  return (
    <Box sx={{ mb: 0.5 }}>
      <Typography sx={{ fontSize: 13.5, color: "text.secondary", mb: 1 }}>
        {label}
      </Typography>
      <Box
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${total > 0 ? (done / total) * 100 : 0}%`,
            bgcolor: "success.main",
            borderRadius: 2,
            transition: "width .3s ease",
          }}
        />
      </Box>
    </Box>
  );
}

const pluralComments = (n: number) => `${n} comment${n === 1 ? "" : "s"}`;

// Glyph (18px) + gap: the indent that lines the caption, thread and
// composer up under the department name.
const ROW_INDENT = "30px";

function threadEntries(
  record: DepartmentImplementation | undefined,
): RecordThreadEntry[] {
  return (record?.comments ?? []).map((c) => ({
    author: c.by_name ?? c.by ?? "Unknown",
    at: c.at ? formatDateTime(c.at) : undefined,
    text: c.text,
  }));
}

/**
 * Status glyph, department name and a one-line caption (who implemented it
 * and when, or "Pending"), with room on the right for the row's actions.
 */
function DepartmentHeadline({
  department,
  record,
  showCommentCount,
  actions,
}: {
  department: string;
  record: DepartmentImplementation | undefined;
  showCommentCount: boolean;
  actions?: React.ReactNode;
}) {
  const implemented = record?.implemented === true;
  const count = record?.comments?.length ?? 0;
  const parts = implemented
    ? [
        `Implemented by ${record?.implemented_by_name ?? "Unknown"}`,
        ...(record?.implemented_at
          ? [formatDateTime(record.implemented_at)]
          : []),
      ]
    : ["Pending"];
  if (showCommentCount && count > 0) parts.push(pluralComments(count));

  return (
    <Stack direction="row" alignItems="flex-start" spacing={1.5}>
      <RavenIcon
        icon={implemented ? CircleCheck : CircleDashed}
        aria-hidden="true"
        sx={{
          fontSize: 18,
          mt: "1px",
          flexShrink: 0,
          color: implemented ? "success.main" : "text.disabled",
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        columnGap={2}
        rowGap={1}
        sx={{ flex: 1, minWidth: 0 }}
      >
        <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 600, lineHeight: "20px" }}
          >
            {displayDepartment(department)}
          </Typography>
          <Typography
            sx={{ fontSize: 12, color: "text.secondary", lineHeight: "18px" }}
          >
            {parts.join(" · ")}
          </Typography>
        </Box>
        {actions}
      </Stack>
    </Stack>
  );
}

/**
 * One department's implementation, read-only with its whole comment
 * thread shown — the same row as the card, for the print view.
 */
export function DepartmentRecord({
  department,
  record,
}: {
  department: string;
  record: DepartmentImplementation | undefined;
}) {
  const entries = threadEntries(record);
  return (
    <Box>
      <DepartmentHeadline
        department={department}
        record={record}
        showCommentCount={false}
      />
      {entries.length > 0 && (
        <Box sx={{ pl: ROW_INDENT, mt: 1.5 }}>
          <RecordThread entries={entries} />
        </Box>
      )}
    </Box>
  );
}

interface RowProps {
  department: string;
  record: DepartmentImplementation | undefined;
  canEdit: boolean;
  saving: boolean;
  composerOpen: boolean;
  onToggleComposer: () => void;
  onSave: (change: ImplementationChange) => void;
}

function DepartmentRow({
  department,
  record,
  canEdit,
  saving,
  composerOpen,
  onToggleComposer,
  onSave,
}: RowProps) {
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const implemented = record?.implemented === true;
  const entries = threadEntries(record);
  const count = entries.length;
  // A marked department's trail is closed (the backend refuses the comment
  // too) until Undo reopens it.
  const canComment = canEdit && !implemented;
  const composing = canComment && composerOpen;
  const open = expanded || composing;

  const submitComment = () => {
    const text = draft.trim();
    if (!text) return;
    onSave({ department, comment: text });
    setDraft("");
  };

  const toggle = () => {
    if (open) {
      setExpanded(false);
      if (composing) {
        setDraft("");
        onToggleComposer();
      }
    } else if (count > 0) {
      setExpanded(true);
    } else {
      onToggleComposer();
    }
  };

  const showToggle = count > 0 || (canComment && !open);
  const actions =
    (canEdit && !composing) || showToggle ? (
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flexShrink: 0, ml: { xs: -1, sm: 0 } }}
      >
        {showToggle && (
          <Button
            variant="text"
            size="small"
            aria-expanded={open}
            startIcon={<RavenIcon icon={MessageSquare} />}
            onClick={toggle}
            sx={{ color: "text.secondary", px: 1, whiteSpace: "nowrap" }}
          >
            {count > 0 ? `Comments (${count})` : "Add comment"}
          </Button>
        )}
        {/* Comment first, then mark: the mark steps aside while this
            row's composer is open. */}
        {canEdit && !composing && (
          <Button
            variant={implemented ? "text" : "outlined"}
            color="primary"
            size="small"
            disabled={saving}
            onClick={() => onSave({ department, implemented: !implemented })}
            sx={{
              whiteSpace: "nowrap",
              ...(implemented && { color: "text.secondary" }),
            }}
          >
            {implemented ? "Undo" : "Mark implemented"}
          </Button>
        )}
      </Stack>
    ) : undefined;

  return (
    <Box
      component="li"
      sx={{
        py: 1.75,
        borderTop: "1px solid",
        borderColor: "divider",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <DepartmentHeadline
        department={department}
        record={record}
        showCommentCount
        actions={actions}
      />

      <Collapse in={open} unmountOnExit>
        <Stack spacing={1.75} sx={{ pl: ROW_INDENT, pt: 1.75 }}>
          <RecordThread label={null} entries={entries} />

          {composing && (
            <Box
              sx={(theme) => ({
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.paper",
                transition: "border-color .15s ease, box-shadow .15s ease",
                "&:focus-within": {
                  borderColor: "primary.main",
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
              })}
            >
              <TextField
                placeholder={`Add a comment for ${displayDepartment(department)}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                autoFocus
                variant="standard"
                slotProps={{
                  input: { disableUnderline: true },
                  htmlInput: {
                    maxLength: COMMENT_MAX,
                    "aria-label": `Comment for ${displayDepartment(department)}`,
                  },
                }}
                sx={{ px: 1.5, pt: 1.25 }}
              />
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 0.75,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}
                >
                  {draft.length}/{COMMENT_MAX}
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  sx={{ color: "text.secondary" }}
                  onClick={() => {
                    setDraft("");
                    onToggleComposer();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={draft.trim().length === 0 || saving}
                  onClick={submitComment}
                >
                  Add comment
                </Button>
              </Stack>
            </Box>
          )}

          {canComment && !composing && count > 0 && (
            <Box>
              <Button
                variant="text"
                size="small"
                startIcon={<RavenIcon icon={MessageSquarePlus} />}
                onClick={onToggleComposer}
                sx={{ color: "primary.dark", px: 1, ml: -1 }}
              >
                Add comment
              </Button>
            </Box>
          )}
        </Stack>
      </Collapse>
    </Box>
  );
}

interface Props {
  suggestion: Suggestion;
}

/**
 * Implementation, department by department. The SC nominates the
 * departments that will verify the benefits; each of them reports here,
 * and the backend closes the stage on its own once the last one is in.
 *
 * Renders from `accepted_by_sc` onward, and only once the SC evaluation
 * actually nominated someone — a suggestion approved without verifier
 * departments has nothing to track. Controls appear for a viewer who
 * passes the `record_department_implementation` probe AND either belongs
 * to the department or acts for the scheme; everyone else sees the same
 * rows read-only. The server enforces both rules — this only keeps a
 * button off a rail where it would 403.
 */
export default function ImplementationCard({ suggestion }: Props) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [composerFor, setComposerFor] = useState<string | null>(null);

  const departments = verifierDepartments(
    suggestionEvaluations(suggestion).ssc,
  );
  const records = suggestionImplementation(suggestion).departments ?? {};

  const authQ = useIsAuthorized(
    {
      action: "record_department_implementation",
      entity_type: "ess_suggestion",
      entity_id: suggestion.id,
      workflow: "ess_suggestion",
    },
    [suggestion.status],
  );
  const { data: profile } = useCurrentUserProfile();
  const { isCustodian } = useIsCustodian();
  const myDepartments = profile?.user_info?.departments ?? [];

  const mutation = useMutation({
    mutationFn: (change: ImplementationChange) =>
      recordDepartmentImplementation(suggestion.id, change),
    onSuccess: (fresh, change) => {
      enqueueSnackbar(
        change.comment ? "Comment added" : "Implementation updated",
        { variant: "success" },
      );
      // Prime the detail cache so the row flips before the refetch lands,
      // same as the committee seats.
      qc.setQueryData(["ess-detail", suggestion.id], fresh);
      invalidateSuggestionCaches(qc, suggestion.id);
      setComposerFor(null);
    },
    onError: (err: unknown) => {
      enqueueSnackbar(
        apiErrorMessage(err, "Could not record the implementation"),
        { variant: "error" },
      );
    },
  });

  if (!VISIBLE_STATES.has(suggestion.status)) return null;
  if (departments.length === 0) return null;

  const done = departments.filter((d) => records[d]?.implemented === true);
  const reporting = suggestion.status === REPORTING_STATE;
  const pending = mutation.isPending ? mutation.variables : undefined;

  // `under_verification` predates per-department reporting: its rows were
  // never reported department by department, so "0 of N implemented" would
  // misstate them. Say so instead of drawing empty rows.
  if (
    suggestion.status === "under_verification" &&
    Object.keys(records).length === 0
  ) {
    return (
      <Card sx={{ mb: 2.5 }} data-tour="detail-implementation">
        <CardContent>
          <SectionHeader title="Implementation" extra="Not tracked per department" />
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            This suggestion reached verification before implementation was
            reported department by department. Verifier departments:{" "}
            {departments.map(displayDepartment).join(", ")}.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2.5 }} data-tour="detail-implementation">
      <CardContent>
        <SectionHeader title="Implementation" />
        <ProgressSummary done={done.length} total={departments.length} />
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {departments.map((department) => {
            // Reporting is the department's own to do; a custodian or
            // admin can record on anyone's behalf. Everything after the
            // reporting stage is the record, not a form.
            const mine = myDepartments.includes(department);
            return (
              <DepartmentRow
                key={department}
                department={department}
                record={records[department]}
                canEdit={
                  reporting &&
                  authQ.data === true &&
                  (mine || isCustodian)
                }
                saving={pending?.department === department}
                composerOpen={composerFor === department}
                onToggleComposer={() =>
                  setComposerFor((cur) =>
                    cur === department ? null : department,
                  )
                }
                onSave={mutation.mutate}
              />
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
